// 🎨 기기 편집 Dialog - 프리미엄 디자인
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  Slide,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  MonetizationOn as MoneyIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import type { DeviceInfo } from '../../../types/application';
import { DEVICE_STATUS_LIST } from '../../../../../constants/statusList';
import { getDeviceStatusColor } from '../../../../../constants/statusColors';

// 🎯 애니메이션 전환
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// 📝 Props 인터페이스
interface DeviceEditDialogProps {
  open: boolean;
  device: DeviceInfo | null;
  onClose: () => void;
  onSave: (deviceId: string, updates: Partial<DeviceInfo>) => Promise<void>;
  loading?: boolean;
}

// 🎯 등급 옵션 (A/B/C/D 4등급 체계)
const GRADE_OPTIONS = ['A', 'B', 'C', 'D'] as const;

const DeviceEditDialog: React.FC<DeviceEditDialogProps> = ({
  open,
  device,
  onClose,
  onSave,
  loading = false
}) => {
  const theme = useTheme();

  // 🔧 상태 관리
  const [formData, setFormData] = useState({
    price_offer: '',
    final_price: '',
    status: '',
    grade: '',
    deduction_reason: '',
    reject_reason: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // 📊 초기 데이터 로딩
  useEffect(() => {
    if (device && open) {
      setFormData({
        price_offer: device.price_offer?.toString() || '',
        final_price: device.final_price?.toString() || '',
        status: device.status || '',
        grade: device.grade || '',
        deduction_reason: device.deduction_reason || '',
        reject_reason: device.reject_reason || '',
      });
      setErrors({});
    }
  }, [device, open]);

  // 🔧 입력 핸들러
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // 💰 가격 입력 핸들러 (숫자만 허용)
  const handlePriceChange = (field: string, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    handleChange(field, numericValue);
  };

  // ✅ 유효성 검사
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.status === '가격안내' && !formData.price_offer) {
      newErrors.price_offer = '가격안내 상태에서는 검수 가격이 필요합니다';
    }

    if (formData.status === '반송' && !formData.reject_reason) {
      newErrors.reject_reason = '반송 사유를 입력해주세요';
    }

    if (formData.price_offer && parseInt(formData.price_offer) < 0) {
      newErrors.price_offer = '가격은 0원 이상이어야 합니다';
    }

    if (formData.final_price && parseInt(formData.final_price) < 0) {
      newErrors.final_price = '가격은 0원 이상이어야 합니다';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 💾 저장 핸들러
  const handleSave = async () => {
    if (!device || !validateForm()) return;

    setSaving(true);
    try {
      const updates: Partial<DeviceInfo> = {
        status: formData.status as DeviceInfo['status'],
        grade: formData.grade as DeviceInfo['grade'],
        deduction_reason: formData.deduction_reason || null,
        reject_reason: formData.reject_reason || null,
      };

      // 가격 필드 처리
      if (formData.price_offer) {
        updates.price_offer = parseInt(formData.price_offer, 10);
      }
      if (formData.final_price) {
        updates.final_price = parseInt(formData.final_price, 10);
      }

      await onSave(device.id, updates);
      onClose();
    } catch (error) {
      console.error('Device save error:', error);
    } finally {
      setSaving(false);
    }
  };

  // 🎨 상태별 색상
  const statusColor = getDeviceStatusColor(formData.status);

  if (!device) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth < 600} // 모바일에서 전체화면
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4 }, // 모바일에서 둥근 모서리 제거
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          overflow: 'visible',
          maxHeight: { xs: '100vh', sm: '90vh' }, // 모바일에서 전체 높이 사용
        },
      }}
    >
      {/* 🎨 헤더 */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              width: 48,
              height: 48,
            }}
          >
            <EditIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              기기 정보 수정
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {device.deviceModel} - {device.deviceType}
            </Typography>
          </Box>
          <Tooltip title="닫기">
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      {/* 📝 컨텐츠 */}
      <DialogContent sx={{ p: 4 }}>
        {/* 🏷️ 현재 상태 표시 */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            현재 상태
          </Typography>
          <Chip
            label={device.status}
            sx={{
              bgcolor: getDeviceStatusColor(device.status),
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2,
              py: 1.5,
            }}
          />
        </Box>

        <Stack spacing={4}>
          {/* 💰 가격 섹션 */}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon sx={{ color: 'primary.main' }} />
              가격 정보
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                label="검수 가격"
                value={formData.price_offer}
                onChange={(e) => handlePriceChange('price_offer', e.target.value)}
                error={!!errors.price_offer}
                helperText={errors.price_offer}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography color="text.secondary" fontWeight={600}>₩</Typography>
                    </InputAdornment>
                  ),
                  inputProps: { 
                    inputMode: 'numeric', 
                    pattern: '[0-9]*',
                    style: { fontSize: window.innerWidth < 600 ? '16px' : '14px' } // iOS 줌 방지
                  }
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    minHeight: { xs: 56, sm: 40 }, // 모바일에서 터치 친화적 높이
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                  },
                }}
                placeholder="검수 완료 시 제안 가격"
              />

              <TextField
                label="최종 가격"
                value={formData.final_price}
                onChange={(e) => handlePriceChange('final_price', e.target.value)}
                error={!!errors.final_price}
                helperText={errors.final_price}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography color="text.secondary" fontWeight={600}>₩</Typography>
                    </InputAdornment>
                  ),
                  inputProps: { 
                    inputMode: 'numeric', 
                    pattern: '[0-9]*',
                    style: { fontSize: window.innerWidth < 600 ? '16px' : '14px' } // iOS 줌 방지
                  }
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    minHeight: { xs: 56, sm: 40 }, // 모바일에서 터치 친화적 높이
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(76, 175, 80, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                  },
                }}
                placeholder="사용자 수락 후 최종 가격"
              />
            </Stack>

            {/* 💡 가격 차이 표시 */}
            {formData.price_offer && formData.final_price && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TrendingUpIcon sx={{ color: 'success.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    가격 차이: 
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight={700}
                    sx={{ 
                      color: parseInt(formData.final_price) >= parseInt(formData.price_offer) 
                        ? 'success.main' 
                        : 'error.main' 
                    }}
                  >
                    ₩{(parseInt(formData.final_price) - parseInt(formData.price_offer)).toLocaleString()}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>

          <Divider />

          {/* 📊 상태 및 등급 섹션 */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <FormControl fullWidth>
              <InputLabel>처리 상태</InputLabel>
              <Select
                value={formData.status}
                label="처리 상태"
                onChange={(e) => handleChange('status', e.target.value)}
                sx={{
                  borderRadius: 2,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: statusColor,
                    boxShadow: `0 0 0 3px ${statusColor}20`,
                  },
                }}
              >
                {DEVICE_STATUS_LIST.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getDeviceStatusColor(status),
                        }}
                      />
                      <Typography>{status}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>등급</InputLabel>
              <Select
                value={formData.grade}
                label="등급"
                onChange={(e) => handleChange('grade', e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <em>등급 미지정</em>
                </MenuItem>
                {GRADE_OPTIONS.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* 📝 사유 섹션 */}
          <Stack spacing={3}>
            <TextField
              label="차감 사유"
              value={formData.deduction_reason}
              onChange={(e) => handleChange('deduction_reason', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              placeholder="가격 차감이 있는 경우 사유를 입력하세요"
            />

            {formData.status === '반송' && (
              <TextField
                label="반송 사유"
                value={formData.reject_reason}
                onChange={(e) => handleChange('reject_reason', e.target.value)}
                error={!!errors.reject_reason}
                helperText={errors.reject_reason}
                multiline
                rows={2}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                placeholder="반송 사유를 상세히 입력하세요"
              />
            )}
          </Stack>

          {/* ⚠️ 경고 메시지 */}
          {formData.status === '반송됨' && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                반송 처리 시 고객에게 알림톡이 자동 발송됩니다.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      {/* 🎯 액션 버튼 - 모바일 최적화 */}
      <DialogActions 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          pt: 0,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' }, // 모바일에서 세로 배치
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          fullWidth={window.innerWidth < 600} // 모바일에서 전체 너비
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            minHeight: { xs: 48, sm: 36 }, // 모바일에서 터치 친화적 높이
            fontSize: { xs: '1rem', sm: '0.875rem' },
            borderWidth: 2,
            '&:hover': { borderWidth: 2 },
            order: { xs: 2, sm: 1 }, // 모바일에서 순서 변경
          }}
        >
          취소
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? undefined : <SaveIcon />}
          fullWidth={window.innerWidth < 600} // 모바일에서 전체 너비
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            minHeight: { xs: 48, sm: 36 }, // 모바일에서 터치 친화적 높이
            fontSize: { xs: '1rem', sm: '0.875rem' },
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              boxShadow: '0 6px 28px rgba(25, 118, 210, 0.4)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              background: 'grey.300',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            order: { xs: 1, sm: 2 }, // 모바일에서 순서 변경 (저장 버튼이 위로)
          }}
        >
          {saving ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceEditDialog;